import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.token) {
        data.reset_url = `${window.location.origin}/reset-password?token=${data.token}`;
      }
      setResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!result?.reset_url) return;
    try {
      await navigator.clipboard.writeText(result.reset_url);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/signin" className="inline-flex items-center gap-2 text-[#52443E] mb-6" data-testid="forgot-back">
          <ArrowLeft size={16} /> Back to sign in
        </Link>

        <div className="card-paper p-8 sm:p-10 paper-grain rise">
          <h1 className="font-serif-display text-3xl text-[#2C221E] leading-tight" data-testid="forgot-title">
            Find your way back
          </h1>
          <p className="mt-3 text-[#73635B]">
            Tell us the email you used, and we'll help you back into your journal.
          </p>

          {!result ? (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#8C7A70] font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  className="input-paper mt-2"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
              {error && <p className="text-sm text-[#C26D53]" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit">
                {loading ? "One moment…" : "Send me a reset link"}
              </button>
            </form>
          ) : (
            <div className="mt-8" data-testid="forgot-result">
              <p className="text-[#2C221E]">{result.message}</p>
              {result.reset_url && (
                <>
                  <div className="mt-5 p-4 rounded-xl border border-[#D8CBB9] bg-[#F9ECE8] text-sm break-all text-[#52443E]" data-testid="forgot-reset-url">
                    {result.reset_url}
                  </div>
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <button className="btn-ghost inline-flex items-center gap-2" onClick={copyLink} data-testid="forgot-copy-link">
                      <Copy size={14} /> Copy link
                    </button>
                    <a
                      href={result.reset_url}
                      className="btn-primary"
                      data-testid="forgot-open-link"
                    >
                      Open link
                    </a>
                  </div>
                  <p className="mt-4 text-xs text-[#8C7A70]">
                    This link expires in {result.expires_in_minutes} minutes.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
